import { type UserPaymentTokenInfo } from '@hooks-api/types/dataModel/eCommercePayments'
import { type CheckboxProps } from '@pets-ui/Checkbox/Checkbox'
import { Checkbox } from '@pets-ui/Checkbox/Checkbox'
import { HStack } from '@pets-ui/HStack/HStack'
import { Info } from '@pets-ui/icons'
import { Radio } from '@pets-ui/Radio/Radio'
import { Select } from '@pets-ui/Select/Select'
import { type SelectOptionValue } from '@pets-ui/SelectOption/SelectOption'
import { SelectOption } from '@pets-ui/SelectOption/SelectOption'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { Tooltip } from '@pets-ui/Tooltip/Tooltip'
import { VStack } from '@pets-ui/VStack/VStack'
import * as stylex from '@stylexjs/stylex'
import { type ReactNode } from 'react'

export interface CreditCardFieldsProps {
  isVisible: boolean
  rememberPaymentCardTokenChecked?: CheckboxProps['checked']
  onRememberPaymentCardTokenChange?: CheckboxProps['onChange']
  canRememberPaymentCardToken?: boolean
  disabledChangeRememberPaymentCard?: boolean
  userPaymentTokenInfos?: UserPaymentTokenInfo[]
  selectedUserPaymentTokenInfo?: SelectOptionValue
  onChangeSelectUserPaymentTokenInfo?: (newValue: SelectOptionValue) => void
  onChangeCardSource?: (value: string) => void
  cardSource?: string | null
  children: ReactNode
  translateText?: Record<string, string>
  renderCheckoutCreditCardRewardContent?: () => ReactNode
  renderFieldsOfRememberedCard?: () => ReactNode
  /** CKO Flow：存卡選擇由 CKO stored_card drop-in 負責，隱藏 Frames 時代的「選擇卡號」選單，只顯示 CKO 容器與記住卡片。 */
  shouldUseCkoFlow?: boolean
}

const baseStyle = stylex.create({
  select: {
    overflow: 'hidden',
    flexWrap: 'nowrap',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: 210,
  },
  tooltip: {
    borderRadius: 4,
    minWidth: 312,
  },
})

/** 卡號來源 */
export const CARD_SOURCE = {
  /** 選擇已記住的卡號 */
  REMEMBERED: 'remembered',
  /** 新增卡號 */
  ADD_CARD: 'addCard',
}

export const CreditCardFields = ({
  isVisible,
  onRememberPaymentCardTokenChange,
  rememberPaymentCardTokenChecked,
  canRememberPaymentCardToken,
  disabledChangeRememberPaymentCard,
  userPaymentTokenInfos = [],
  selectedUserPaymentTokenInfo = null,
  onChangeSelectUserPaymentTokenInfo,
  onChangeCardSource,
  cardSource = CARD_SOURCE.ADD_CARD,
  children,
  translateText,
  renderFieldsOfRememberedCard,
  renderCheckoutCreditCardRewardContent,
  shouldUseCkoFlow = false,
}: CreditCardFieldsProps) => {
  const tooltipText = translateText?.disableChangeCardTooltipText

  return (
    <>
      <VStack display={isVisible ? 'block' : 'none'}>
        {!shouldUseCkoFlow && userPaymentTokenInfos.length > 0 && (
          <VStack alignItems="flex-start">
            <VStack gap="8px">
              <HStack gap="8px">
                <Radio label={translateText?.selectCardNumber} checked={cardSource === CARD_SOURCE.REMEMBERED} color="blue" onChange={() => onChangeCardSource?.(CARD_SOURCE.REMEMBERED)} />
                <Select disabled={cardSource === CARD_SOURCE.ADD_CARD} value={selectedUserPaymentTokenInfo} onChange={onChangeSelectUserPaymentTokenInfo} xStyles={{ select: baseStyle.select }} placeholder={translateText?.pleaseSelect}>
                  {userPaymentTokenInfos.map((card) => {
                    return (
                      <SelectOption key={card.payment_token_id} value={card.payment_token_id}>
                        {`${card.issuer_info?.issuer_zh_tw || card.issuer_info?.issuer || card.card_type} *${card.payment_token_card_no} (${card.card_type}, ${translateText?.exp} ${card.expire_month}/${String(card.expire_year).slice(-2)})`}
                      </SelectOption>
                    )
                  })}
                </Select>
              </HStack>
              {cardSource === CARD_SOURCE.REMEMBERED && renderFieldsOfRememberedCard?.()}
            </VStack>
            <Radio label={translateText?.addNewCard} checked={cardSource === CARD_SOURCE.ADD_CARD} color="blue" onChange={() => onChangeCardSource?.(CARD_SOURCE.ADD_CARD)} />
            {cardSource === CARD_SOURCE.REMEMBERED && renderCheckoutCreditCardRewardContent?.()}
          </VStack>
        )}
        <VStack display={shouldUseCkoFlow || cardSource === CARD_SOURCE.ADD_CARD ? 'block' : 'none'}>
          {children}
          {(shouldUseCkoFlow || cardSource === CARD_SOURCE.ADD_CARD) && renderCheckoutCreditCardRewardContent?.()}
          {onRememberPaymentCardTokenChange && canRememberPaymentCardToken && (
            <HStack mt={2} gap={2} alignItems="center">
              <Checkbox label={translateText?.rememberCard} labelColor={colors.grey600} color={disabledChangeRememberPaymentCard ? 'grey' : 'blue'} onChange={onRememberPaymentCardTokenChange} checked={rememberPaymentCardTokenChecked} disabled={disabledChangeRememberPaymentCard} />
              {disabledChangeRememberPaymentCard
              && (
                <Tooltip
                  variant="dark"
                  paperStyle={baseStyle.tooltip}
                  title={(
                    <Text variant="body">
                      {tooltipText}
                    </Text>
                  )}
                  placement="bottom"
                  offset={[0, 16]}
                  disablePortal={false}
                >
                  <Info fontSize={16} />
                </Tooltip>
              )}
            </HStack>
          )}
        </VStack>
      </VStack>
    </>
  )
}
